import { useId } from "react";
import { GrafikCercevesi } from "./GrafikCercevesi";
import { GrafikDesenleri } from "./grafik-svg";
import { olculen, sayiMetni } from "./hesap";
import type { DagilimDilimi, GrafikOrtakProps } from "./tipler";

export interface DagilimCubuguProps extends GrafikOrtakProps {
  /** Sayımlar veya ağırlıklar: yüzdeler geçerli değerlerin toplamına göre hesaplanır. */
  dilimler: readonly DagilimDilimi[];
}

export function DagilimCubugu({
  baslik,
  dilimler,
  ozet,
  birim = "",
  bosMesaji,
}: DagilimCubuguProps) {
  const kimlik = `dagilim-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const degerler = dilimler.map((dilim) => olculen(dilim.deger));
  const enBuyuk = degerler.reduce<number>((enCok, deger) => Math.max(enCok, deger ?? 0), 0);
  // Toplam çok büyük olsa da oran sonlu kalmalı; pay ve payda birlikte ölçeklenir.
  const agirliklar = degerler.map((deger) => (enBuyuk === 0 ? 0 : (deger ?? 0) / enBuyuk));
  const toplam = agirliklar.reduce((birikim, deger) => birikim + deger, 0);
  const yuzdeler = degerler.map((deger, sira) =>
    deger === null || toplam === 0 ? null : ((agirliklar[sira] ?? 0) / toplam) * 100,
  );
  const ozetMetni =
    ozet?.trim() ||
    `${baslik}: ${dilimler.map((dilim, sira) => `${dilim.etiket} ${sayiMetni(degerler[sira], birim)} (${sayiMetni(yuzdeler[sira], "%")})`).join("; ") || "Ölçülmedi"}.${degerler.some((deger) => deger === null) ? " Eksik ölçümler dağılım hesabına katılmadı." : ""}`;
  let sol = 0;
  const seriler = dilimler.map((dilim, sira) => ({
    anahtar: String(sira),
    etiket: dilim.etiket,
    ton: dilim.ton,
  }));
  return (
    <GrafikCercevesi
      baslik={baslik}
      ozet={ozetMetni}
      bos={toplam === 0}
      bosMesaji={
        bosMesaji ??
        (degerler.some((deger) => deger !== null)
          ? "Dağılım toplamı sıfır; yüzde hesaplanamıyor"
          : "Henüz ölçüm yok")
      }
      basliklar={["Değer", "Pay"]}
      satirlar={dilimler.map((dilim, sira) => ({
        anahtar: String(sira),
        etiket: dilim.etiket,
        hucreler: [sayiMetni(degerler[sira], birim), sayiMetni(yuzdeler[sira], "%")],
      }))}
    >
      <svg
        role="img"
        aria-label={ozetMetni}
        viewBox="0 0 360 48"
        width="100%"
        style={{ display: "block", height: "auto" }}
      >
        <title>{ozetMetni}</title>
        <GrafikDesenleri kimlik={kimlik} seriler={seriler} />
        {dilimler.map((dilim, sira) => {
          const genislik = ((yuzdeler[sira] ?? 0) / 100) * 360;
          const baslangic = sol;
          sol += genislik;
          return (
            <g key={sira}>
              <rect
                data-testid="dagilim-dilimi"
                x={baslangic}
                y="8"
                width={genislik}
                height="32"
                fill={`url(#${kimlik}-${sira})`}
                stroke="var(--color-notr-0)"
              >
                <title>
                  {sira + 1}. {dilim.etiket}: {sayiMetni(degerler[sira], birim)};{" "}
                  {sayiMetni(yuzdeler[sira], "%")}
                </title>
              </rect>
              {genislik >= 24 && (
                <text
                  x={baslangic + genislik / 2}
                  y="30"
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill="var(--color-notr-900)"
                  stroke="var(--color-notr-0)"
                  strokeWidth="3"
                  paintOrder="stroke"
                >
                  {sira + 1}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div
        className="text-notr-800 flex flex-wrap gap-2 text-sm"
        style={{ overflowWrap: "anywhere" }}
      >
        {dilimler.map((dilim, sira) => (
          <span key={sira}>
            {sira + 1}. {dilim.etiket}: {sayiMetni(yuzdeler[sira], "%")}
          </span>
        ))}
      </div>
      {degerler.some((deger) => deger === null) && (
        <p className="text-notr-600 text-sm">Eksik ölçümler dağılım hesabına katılmadı.</p>
      )}
    </GrafikCercevesi>
  );
}
